import React, { useRef, useMemo } from 'react';
import { Canvas, useFrame } from '@react-three/fiber';
import * as THREE from 'three';

// Fractal shader materials
const fractalShaders = {
    flame: {
        vertexShader: `
            varying vec2 vUv;
            void main() {
                vUv = uv;
                gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
            }
        `,
        fragmentShader: `
            uniform float time;
            uniform float intensity;
            uniform vec3 palette[3];
            uniform bool reduceMotion;
            varying vec2 vUv;

            float noise(vec2 p) {
                return fract(sin(dot(p, vec2(12.9898, 78.233))) * 43758.5453);
            }

            float fbm(vec2 p) {
                float value = 0.0;
                float amplitude = 0.5;
                for(int i = 0; i < 4; i++) {
                    value += amplitude * noise(p);
                    p *= 2.0;
                    amplitude *= 0.5;
                }
                return value;
            }

            void main() {
                vec2 uv = vUv * 2.0 - 1.0;
                float t = reduceMotion ? time * 0.1 : time * 0.5;

                float dist = length(uv);
                float angle = atan(uv.y, uv.x);

                float flame = fbm(vec2(dist * 3.0 - t, angle + t * 0.5));
                flame *= (1.0 - dist * 0.8) * intensity;
                flame = smoothstep(0.0, 1.0, flame);

                vec3 color = mix(palette[0], palette[1], flame);
                color = mix(color, palette[2], flame * flame);

                float alpha = flame * 0.8;
                gl_FragColor = vec4(color, alpha);
            }
        `
    },
    kaleidoscope: {
        vertexShader: `
            varying vec2 vUv;
            void main() {
                vUv = uv;
                gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
            }
        `,
        fragmentShader: `
            uniform float time;
            uniform float intensity;
            uniform vec3 palette[3];
            uniform bool pulseOnBeat;
            uniform bool reduceMotion;
            varying vec2 vUv;

            vec2 kaleidoscope(vec2 uv, float segments) {
                float angle = atan(uv.y, uv.x);
                float radius = length(uv);

                angle = mod(angle, 2.0 * 3.14159 / segments);
                if(mod(floor(angle / (2.0 * 3.14159 / segments)), 2.0) == 1.0) {
                    angle = 2.0 * 3.14159 / segments - angle;
                }

                return vec2(cos(angle), sin(angle)) * radius;
            }

            float pattern(vec2 uv, float time) {
                uv = kaleidoscope(uv, 6.0);

                float wave1 = sin(uv.x * 10.0 + time);
                float wave2 = cos(uv.y * 8.0 - time * 0.7);
                float wave3 = sin(length(uv) * 15.0 - time * 1.5);

                return (wave1 + wave2 + wave3) / 3.0;
            }

            void main() {
                vec2 uv = (vUv - 0.5) * 2.0;
                float t = reduceMotion ? time * 0.2 : time;

                float pulse = pulseOnBeat ? (sin(t * 2.0) * 0.5 + 0.5) : 1.0;
                float p = pattern(uv, t) * intensity * pulse;

                vec3 color = mix(palette[0], palette[1], (p + 1.0) / 2.0);
                color = mix(color, palette[2], abs(p));

                float alpha = (abs(p) + 0.2) * 0.7;
                gl_FragColor = vec4(color, alpha);
            }
        `
    },
    lotus: {
        vertexShader: `
            varying vec2 vUv;
            void main() {
                vUv = uv;
                gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
            }
        `,
        fragmentShader: `
            uniform float time;
            uniform float intensity;
            uniform vec3 palette[3];
            uniform bool reduceMotion;
            varying vec2 vUv;

            float sdCircle(vec2 p, float r) {
                return length(p) - r;
            }

            float lotus(vec2 uv, float time) {
                float angle = atan(uv.y, uv.x);
                float radius = length(uv);

                float petals = 8.0;
                float petalShape = sin(angle * petals + time) * 0.3 + 0.7;
                float center = smoothstep(0.1, 0.3, radius);

                float lotus = smoothstep(petalShape, petalShape - 0.1, radius) * center;
                return lotus;
            }

            void main() {
                vec2 uv = (vUv - 0.5) * 3.0;
                float t = reduceMotion ? time * 0.1 : time * 0.3;

                float lotusShape = lotus(uv, t);
                lotusShape *= intensity;

                vec3 color = mix(palette[0], palette[1], lotusShape);
                color = mix(color, palette[2], lotusShape * lotusShape);

                float alpha = lotusShape * 0.8;
                gl_FragColor = vec4(color, alpha);
            }
        `
    }
};

// Color palettes
const palettes = {
    ember: [
        new THREE.Vector3(0.1, 0.0, 0.0),  // Dark red
        new THREE.Vector3(1.0, 0.3, 0.0),  // Orange
        new THREE.Vector3(1.0, 1.0, 0.3)   // Yellow
    ],
    ruby: [
        new THREE.Vector3(0.2, 0.0, 0.1),  // Dark crimson
        new THREE.Vector3(0.8, 0.1, 0.3),  // Ruby
        new THREE.Vector3(1.0, 0.6, 0.8)   // Pink
    ],
    ocean: [
        new THREE.Vector3(0.0, 0.1, 0.3),  // Deep blue
        new THREE.Vector3(0.0, 0.4, 0.8),  // Ocean blue
        new THREE.Vector3(0.3, 0.8, 1.0)   // Light blue
    ],
    teal: [
        new THREE.Vector3(0.0, 0.2, 0.2),  // Dark teal
        new THREE.Vector3(0.0, 0.6, 0.6),  // Teal
        new THREE.Vector3(0.4, 1.0, 1.0)   // Cyan
    ],
    gold: [
        new THREE.Vector3(0.2, 0.1, 0.0),  // Dark gold
        new THREE.Vector3(1.0, 0.8, 0.2),  // Gold
        new THREE.Vector3(1.0, 1.0, 0.8)   // Light gold
    ]
};

const FractalMesh = ({ params, time, reduceMotion }) => {
    const meshRef = useRef();
    const materialRef = useRef();

    const material = useMemo(() => {
        const shaderType = params.fractal || 'flame';
        const palette = palettes[params.palette] || palettes.ember;

        return new THREE.ShaderMaterial({
            ...fractalShaders[shaderType],
            uniforms: {
                time: { value: 0 },
                intensity: { value: params.intensity || 0.8 },
                palette: { value: palette },
                pulseOnBeat: { value: params.pulseOnBeat || false },
                reduceMotion: { value: reduceMotion }
            },
            transparent: true,
            blending: THREE.AdditiveBlending
        });
    }, [params, reduceMotion]);

    useFrame((state) => {
        if (materialRef.current) {
            materialRef.current.uniforms.time.value = time || state.clock.elapsedTime * (params.speed || 1);
        }
    });

    return (
        <mesh ref={meshRef}>
            <planeGeometry args={[4, 4]} />
            <primitive object={material} ref={materialRef} />
        </mesh>
    );
};

const PostProcessing = ({ theme, intensity }) => {
    const meshRef = useRef();

    const material = useMemo(() => {
        return new THREE.ShaderMaterial({
            vertexShader: `
                varying vec2 vUv;
                void main() {
                    vUv = uv;
                    gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
                }
            `,
            fragmentShader: `
                uniform sampler2D tDiffuse;
                uniform float bloomStrength;
                uniform float vignetteStrength;
                varying vec2 vUv;

                void main() {
                    vec4 color = texture2D(tDiffuse, vUv);

                    // Simple bloom effect
                    vec4 bloom = color * bloomStrength;
                    color += bloom;

                    // Vignette
                    float vignette = distance(vUv, vec2(0.5));
                    vignette = 1.0 - vignette * vignetteStrength;
                    color.rgb *= vignette;

                    gl_FragColor = color;
                }
            `,
            uniforms: {
                bloomStrength: { value: theme === 'wrathful' ? 0.3 : 0.15 },
                vignetteStrength: { value: 0.4 }
            }
        });
    }, [theme]);

    return null; // Simplified for now - full post-processing would require more setup
};

const FractalCanvas = ({ theme, params, time, reduceMotion = false }) => {
    return (
        <Canvas
            camera={{ position: [0, 0, 5], fov: 75 }}
            style={{ width: '100%', height: '100%' }}
        >
            <FractalMesh
                params={params}
                time={time}
                reduceMotion={reduceMotion}
            />
            <PostProcessing theme={theme} intensity={params.intensity} />
        </Canvas>
    );
};

export default FractalCanvas;